import type { CallNotificationItem, IncidentId } from '../realtime/contracts';
import { ObservableStore } from './observableStore';
import { applyCallUpdate, toIncidentCall } from './callProjection';
import type { IncidentCall, IncidentCallUpdate } from './callProjection';

export type CallsSnapshot = {
  readonly calls: readonly CallNotificationItem[];
  readonly callsByIncidentId: ReadonlyMap<IncidentId, IncidentCall>;
};

const EMPTY: CallsSnapshot = {
  calls: [],
  callsByIncidentId: new Map(),
};

function byStartTimeDescending(left: CallNotificationItem, right: CallNotificationItem): number {
  return Date.parse(right.startTimeUtc) - Date.parse(left.startTimeUtc);
}

export class CallStore extends ObservableStore<CallsSnapshot> {
  public constructor() {
    super(EMPTY);
  }

  public static get emptySnapshot(): CallsSnapshot {
    return EMPTY;
  }

  public upsertCall(call: CallNotificationItem): void {
    const { calls } = this.getSnapshot();

    this.publish({
      calls: [...calls.filter((candidate) => candidate.id !== call.id), call].sort(byStartTimeDescending),
    });
  }

  public getIncidentCall(incidentId: IncidentId): IncidentCall | null {
    return this.getSnapshot().callsByIncidentId.get(incidentId) ?? null;
  }

  public hasIncidentCall(incidentId: IncidentId): boolean {
    return this.getSnapshot().callsByIncidentId.has(incidentId);
  }

  public applyIncidentCallUpdate(incidentId: IncidentId, update: IncidentCallUpdate): void {
    const updated = applyCallUpdate(this.getIncidentCall(incidentId), update);
    if (updated === null) {
      return;
    }

    this.setIncidentCall(incidentId, updated);
  }

  public seedIncidentCall(
    incidentId: IncidentId,
    call: CallNotificationItem | null,
    replay: readonly IncidentCallUpdate[],
  ): IncidentCall | null {
    const seeded = call === null ? null : toIncidentCall(call);
    const replayed = replay.reduce<IncidentCall | null>(
      (current, update) => applyCallUpdate(current, update) ?? current,
      seeded,
    );

    this.setIncidentCall(incidentId, replayed);
    return replayed;
  }

  private setIncidentCall(incidentId: IncidentId, call: IncidentCall | null): void {
    const callsByIncidentId = new Map(this.getSnapshot().callsByIncidentId);
    if (call === null) {
      callsByIncidentId.delete(incidentId);
    } else {
      callsByIncidentId.set(incidentId, call);
    }

    this.publish({ callsByIncidentId });
  }
}
